import { useState, useEffect, useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import aemMock from "../../public/aemMock.json";
import FrenchContent from "../../locales/fr/translation.json";
import { fetchAemContent } from "../services/aemContent";
import type { AemContent } from "#types/components/core.type.ts";
import { ELAVON_COOKIE_DOMAIN } from "#constants/index.ts";
import { getCookie, setCookie } from "#utils/privacyChoices.ts";
import { getStoredLanguage, setStoredLanguage, onLanguageStorageChange } from "@repo/ui/lib/storage";
import {
    buildCanonicalWelcomePath,
    getLanguageFromPathname,
    languageFromGeoCountry,
    normalizeLanguage,
    type SupportedLanguage,
} from "#utils/localeRouting.ts";

const WELCOME_LOCALE_COOKIE_KEY = "welcome_locale_preference";

const getRuntimeConf = (): Record<string, string> => {
    return (globalThis as typeof globalThis & { __conf__?: Record<string, string> }).__conf__ ?? {};
};

const getFallbackMock = (language: string): AemContent => {
    return language === "fr-ca" ? FrenchContent : aemMock;
};

const getCookieDomain = (): string => {
    return import.meta.env.MODE === "local" ? "" : ELAVON_COOKIE_DOMAIN;
};

const getGeoCountrySuggestion = (): string | null => {
    const runtimeConf = getRuntimeConf();
    const countryFromConf = runtimeConf.VITE_AKAMAI_COUNTRY || runtimeConf.VITE_COUNTRY_CODE;
    if (countryFromConf) {
        return String(countryFromConf).toLowerCase();
    }

    const cookieCandidates = ["geo_country", "akamai_country_code", "country"];
    for (const cookieName of cookieCandidates) {
        const cookieValue = getCookie(cookieName);
        if (cookieValue) {
            return cookieValue.toLowerCase();
        }
    }

    return null;
};

const getPersistedLanguage = (): SupportedLanguage | null => {
    if (globalThis.window === undefined || globalThis.localStorage === undefined) {
        return null;
    }

    try {
        // Check shared cross-app storage key first
        const stored = getStoredLanguage();
        if (stored && (stored === "en-us" || stored === "en-ca" || stored === "fr-ca")) {
            return stored;
        }

        // Check legacy keys for backwards compatibility
        const legacyLanguage = globalThis.localStorage.getItem("language");
        if (legacyLanguage && (legacyLanguage === "en-us" || legacyLanguage === "en-ca" || legacyLanguage === "fr-ca")) {
            return legacyLanguage;
        }

        const legacyLocale = globalThis.localStorage.getItem("welcome_locale_preference");
        if (legacyLocale && (legacyLocale === "en-us" || legacyLocale === "en-ca" || legacyLocale === "fr-ca")) {
            return legacyLocale;
        }
    } catch {
        return null;
    }

    const cookieLanguage = getCookie(WELCOME_LOCALE_COOKIE_KEY);
    if (cookieLanguage && (cookieLanguage === "en-us" || cookieLanguage === "en-ca" || cookieLanguage === "fr-ca")) {
        return cookieLanguage;
    }

    return null;
};

const persistLanguageSelection = (language: SupportedLanguage): void => {
    if (globalThis.window !== undefined && globalThis.localStorage !== undefined) {
        try {
            // Persist to shared cross-app storage
            setStoredLanguage(language);
            // Also store in legacy keys for backwards compatibility
            globalThis.localStorage.setItem("language", language);
        } catch {
            // Ignore local storage access errors.
        }
    }

    setCookie(WELCOME_LOCALE_COOKIE_KEY, language, 60 * 24 * 365, "/", getCookieDomain());
};

const resolveInitialLanguage = (): SupportedLanguage => {
    const persistedLanguage = getPersistedLanguage();
    if (persistedLanguage) {
        return persistedLanguage;
    }

    const languageFromPath = getLanguageFromPathname(globalThis.location.pathname);
    if (languageFromPath) {
        return languageFromPath;
    }

    return languageFromGeoCountry(getGeoCountrySuggestion());
};

let cachedLang: string | null = null;
let cachedContent: AemContent | null = null; // Cache for AEM content
let cachedMockData: AemContent | null = null; // Cache for mock data
let fetchPromise: Promise<AemContent> | null = null; // Cache for fetch promise
let mockFetchPromise: Promise<AemContent | null> | null = null; // Cache for mock fetch promise

export const useAemContent = () => {
    const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(resolveInitialLanguage);
    const runtimeConf = getRuntimeConf();
    const baseMockUrl = runtimeConf.VITE_AEM_MOCK_URL;
    if (!baseMockUrl) {
        throw new Error("VITE_AEM_MOCK_URL is not defined in environment variables.");
    }
    const aemMockURL = `${baseMockUrl}/${selectedLanguage}/smb-portal/global-shared/dev-managed/welcome-aemMock.json`;
    const [aemContent, setAemContent] = useState<AemContent | null>(null);
    const [aemMockData, setAemMockData] = useState<AemContent | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const setLanguage: Dispatch<SetStateAction<string>> = useCallback((value) => {
        setSelectedLanguage((currentLanguage) => {
            const nextLanguage = typeof value === "function" ? value(currentLanguage) : value;
            return normalizeLanguage(nextLanguage);
        });
    }, []);

    useEffect(() => {
        const canonicalPath = buildCanonicalWelcomePath(globalThis.location.pathname, selectedLanguage);
        if (globalThis.location.pathname !== canonicalPath) {
            globalThis.history.replaceState({}, "", `${canonicalPath}${globalThis.location.search}${globalThis.location.hash}`);
        }

        persistLanguageSelection(selectedLanguage);
    }, [selectedLanguage]);

    useEffect(() => {
        setLoading(true);
        const loadAllData = async () => {
            // Check cache first
            if (cachedContent && cachedLang && selectedLanguage === cachedLang) {
                setAemContent(cachedContent);
                setLoading(false);
                return;
            }

            // Load mock data from URL first
            let mockDataFromUrl: AemContent | null = null;
            
            // Check if we already have cached mock data for this language
            if (cachedMockData && cachedLang === selectedLanguage) {
                mockDataFromUrl = cachedMockData;
                setAemMockData(mockDataFromUrl);
            } else {
                // Need to fetch new mock data — deduplicate concurrent requests
                if (mockFetchPromise === null) {
                    mockFetchPromise = fetch(aemMockURL, {
                        method: "GET",
                        mode: "cors",
                        headers: {
                            "Content-Type": "application/json",
                            "Accept": "application/json",
                        }
                    })
                        .then(async (response) => {
                            if (response.ok) {
                                const data = await response.json();
                                cachedMockData = data;
                                cachedLang = selectedLanguage;
                                return data;
                            }
                            return getFallbackMock(selectedLanguage);
                        })
                        .catch((error) => {
                            console.error("Failed to fetch mock data from URL:", error);
                            return getFallbackMock(selectedLanguage);
                        })
                        .finally(() => {
                            mockFetchPromise = null;
                        });
                }
                mockDataFromUrl = await mockFetchPromise;
                setAemMockData(mockDataFromUrl);
            }

            // Now fetch AEM content with the mock data
            fetchPromise ??= fetchAemContent(selectedLanguage, mockDataFromUrl || getFallbackMock(selectedLanguage))
                .then((data) => {
                    cachedContent = data;
                    cachedLang = selectedLanguage;
                    return data;
                })
                .catch((error) => {
                    console.error("Failed to fetch AEM content. Using fallback data. Error details:", error);
                    return getFallbackMock(selectedLanguage);
                })
                .finally(() => {
                    fetchPromise = null;
                });
            
            try {
                const data = await fetchPromise;
                setAemContent(data);
            } finally {
                setLoading(false);
            }
        };
        
        loadAllData();
    }, [selectedLanguage, aemMockURL]);

    // Listen for language changes from other apps/tabs
    useEffect(() => {
        const unsubscribe = onLanguageStorageChange((newLanguage) => {
            if (newLanguage && (newLanguage === "en-us" || newLanguage === "en-ca" || newLanguage === "fr-ca")) {
                setLanguage(newLanguage);
            }
        });
        return unsubscribe;
    }, [setLanguage]);

    return { aemContent, aemMockData, loading, language: selectedLanguage, setLanguage };
};
